import React, { useState, useEffect } from 'react';
import {
  useDataProvider,
  useNotify,
  useInput,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

interface ServiceProviderSelectorProps {
  source: string;
  label?: string;
  value?: number | null;
  onChange?: (value: number | null) => void;
}

interface ServiceProvider {
  id: number;
  name: string;
  address?: string;
  city?: string;
  provinceState?: string;
  country?: string;
  phoneNumber?: string;
  active: boolean;
}

export const ServiceProviderSelector: React.FC<ServiceProviderSelectorProps> = ({
  source,
  label = 'Service Provider',
  value,
  onChange,
}) => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(value || null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    provinceState: '',
    country: 'Canada',
    phoneNumber: '',
    active: true,
  });

  // Load service providers
  const loadProviders = async () => {
    try {
      setLoading(true);
      const result = await dataProvider.getList('service-providers', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { active: true },
      });
      setProviders(result.data);
    } catch (error) {
      console.error('Error loading service providers:', error);
      notify('Error loading service providers', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    setSelectedProviderId(value || null);
  }, [value]);

  const handleSelect = (providerId: number) => {
    setSelectedProviderId(providerId);
    onChange?.(providerId);
  };

  const handleClearSelection = () => {
    setSelectedProviderId(null);
    onChange?.(null);
  };

  const handleCreateNew = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData({
      name: '',
      address: '',
      city: '',
      provinceState: '',
      country: 'Canada',
      phoneNumber: '',
      active: true,
    });
  };

  const handleFormChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      notify('Business name is required', { type: 'error' });
      return;
    }

    try {
      setCreating(true);
      const result = await dataProvider.create('service-providers', { data: formData });
      
      // Add the new provider to the list
      const newProvider = result.data as ServiceProvider;
      setProviders(prev => [newProvider, ...prev]);
      
      // Auto-select the new provider
      setSelectedProviderId(newProvider.id);
      onChange?.(newProvider.id);
      
      notify('Service provider created successfully', { type: 'success' });
      handleDialogClose();
    } catch (error) {
      console.error('Error creating service provider:', error);
      notify('Error creating service provider', { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const selectedProvider = providers.find(p => p.id === selectedProviderId);

  const formatAddress = (provider: ServiceProvider) => {
    const parts = [provider.address, provider.city, provider.provinceState].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '';
  };

  // Filter providers based on search term
  const filteredProviders = providers.filter(provider => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      provider.name.toLowerCase().includes(term) ||
      (provider.address && provider.address.toLowerCase().includes(term)) ||
      (provider.city && provider.city.toLowerCase().includes(term)) ||
      (provider.provinceState && provider.provinceState.toLowerCase().includes(term))
    );
  });

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      
      {/* Selected Provider Display */}
      {selectedProvider && (
        <Paper 
          sx={{ 
            p: 2.5, 
            mb: 2, 
            border: '2px solid #4caf50',
            backgroundColor: '#f1f8e9',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckIcon sx={{ color: '#4caf50', mt: 0.5, fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  {selectedProvider.name}
                </Typography>
                {formatAddress(selectedProvider) && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 14 }} />
                    {formatAddress(selectedProvider)}
                  </Typography>
                )}
                {selectedProvider.phoneNumber && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 14 }} />
                    {selectedProvider.phoneNumber}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button 
              size="small" 
              onClick={handleClearSelection} 
              variant="outlined"
              sx={{ 
                borderColor: '#4caf50',
                color: '#2e7d32',
                '&:hover': {
                  borderColor: '#2e7d32',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                }
              }}
            >
              Change
            </Button>
          </Box>
        </Paper>
      )}

      {/* Service Provider List */}
      {!selectedProvider && (
        <Paper sx={{ border: '1px solid #e0e0e0' }}>
          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              placeholder="Search existing providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#9e9e9e', mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white'
                }
              }}
            />
          </Box>

          {/* Create New Button */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
            <Button
              onClick={handleCreateNew}
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              sx={{ 
                backgroundColor: '#2196f3',
                '&:hover': { backgroundColor: '#1976d2' },
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5
              }}
            >
              Create New Service Provider
            </Button>
          </Box>

          {/* Provider List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredProviders.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                {searchTerm ? 'No providers match your search.' : 'No service providers found.'}
              </Typography>
              {searchTerm && (
                <Typography variant="caption" color="text.secondary">
                  Try a different search term or create a new provider above.
                </Typography>
              )}
            </Box>
          ) : (
            <>
              {/* Results count */}
              <Box sx={{ px: 2, py: 1, backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="caption" color="text.secondary">
                  {searchTerm ? `${filteredProviders.length} provider${filteredProviders.length !== 1 ? 's' : ''} found` : `${filteredProviders.length} provider${filteredProviders.length !== 1 ? 's' : ''} available`}
                </Typography>
              </Box>
              
              <List sx={{ maxHeight: 250, overflow: 'auto', py: 0 }}>
                {filteredProviders.map((provider, index) => (
                  <React.Fragment key={provider.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleSelect(provider.id)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <BusinessIcon sx={{ color: '#757575', fontSize: 20 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {provider.name}
                            </Typography>
                            {formatAddress(provider) && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {formatAddress(provider)}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                            Select
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < filteredProviders.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Paper>
      )}

      {/* Create Provider Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6" component="span">
              Add New Service Provider
            </Typography>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Business Name"
                  value={formData.name}
                  onChange={handleFormChange('name')}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={formData.address}
                  onChange={handleFormChange('address')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={formData.city}
                  onChange={handleFormChange('city')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Province/State"
                  value={formData.provinceState}
                  onChange={handleFormChange('provinceState')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Country"
                  value={formData.country}
                  onChange={handleFormChange('country')}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="US">United States</option>
                  <option value="Canada">Canada</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleFormChange('phoneNumber')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={creating || !formData.name.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ minWidth: 140 }}
          >
            {creating ? 'Creating...' : 'Create & Select'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};